import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Launch, ShareOutlined } from "@material-ui/icons";
import React from "react";

import { Brand } from "@hubcapp/graphql/types/gateway";
import { BusinessProfileUrlCardProps } from "../../types";
import { useSnackbar } from "notistack";

const useStyles = makeStyles(theme => ({
  cardContent: {
    padding: 0,
  },
  item: {
    padding: theme.spacing(3, 3, 0, 2),
  },
  title: {
    padding: theme.spacing(0),
    marginBottom: theme.spacing(2),
  },
  cardActions: {
    margin: theme.spacing(2, 0, 0, 0),
  },
  profileUrlTextfield: {
    backgroundColor: theme.palette.grey[100],
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  adornedEnd: {
    paddingRight: 0,
  },
  share: {
    lineHeight: "100%",
  },
  separator: {
    margin: theme.spacing(4, 0),
    "& hr": {
      border: "none",
      borderTop: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  label: {
    margin: theme.spacing(0, 0, 1, 0),
  },
}));

const BusinessProfileUrl: React.FC<BusinessProfileUrlCardProps> = ({
  profileUrls,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const handleIconClick = (url: string) => {
    window.open(url, "_blank");
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar("URL copied to clipboard!", {
        variant: "success",
        autoHideDuration: 2000,
      });
    } catch (error) {
      enqueueSnackbar("Failed to copy URL to clipboard!", {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
  };

  const handleShareClick = async (url: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Business Profile",
          text: "Check out my business profile",
          url,
        });
      } catch (error) {
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  return (
    <Card data-test="business-profile-url">
      <CardContent className={classes.cardContent}>
        <Grid container direction={"row"} wrap="wrap" justify="space-between">
          <Grid key="1" item sm={12} xs={12} className={classes.item}>
            <Typography
              variant="h2"
              color="textSecondary"
              className={classes.title}
            >
              My Business Profile
            </Typography>

            {profileUrls?.map((profileUrl, i) => (
              <div key={profileUrl.brand}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.label}
                >
                  {profileUrl.brand === Brand.Aads ? "The AA" : null}
                  {profileUrl.brand === Brand.Bsm ? "BSM" : null}
                </Typography>
                <TextField
                  value={profileUrl.url}
                  variant="outlined"
                  disabled={true}
                  fullWidth
                  className={classes.profileUrlTextfield}
                  InputProps={{
                    classes: {
                      adornedEnd: classes.adornedEnd,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleIconClick(profileUrl.url)}
                        >
                          <Launch />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <div className={classes.cardActions}>
                  <Button
                    variant="contained"
                    color="secondary"
                    classes={{
                      label: classes.addButton,
                    }}
                    onClick={() => handleShareClick(profileUrl.url)}
                  >
                    <ShareOutlined fontSize="small" />
                    <span className={classes.share}>Share</span>
                  </Button>
                </div>

                {i < profileUrls.length - 1 && (
                  <div className={classes.separator}>
                    <hr />
                  </div>
                )}
              </div>
            ))}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default BusinessProfileUrl;
